const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const STORAGE_KEY = PersistenceConfig.keys.debug.clientSession;

const internals = {};

module.exports = {
    STORAGE_KEY,
    getActiveSession,
    hasActiveSession,
    saveSession,
    clearActiveSession,
    getDebugCodeFromLocation,
    getDebugCodeFromUrl,
    hasDebugLinkInLocation,
    isDebugUrl,
    activateFromLocation,
    activateFromUrl,
    submitReport,
    getConsoleActivationSnapshot
};

function getActiveSession() {

    if (typeof window === 'undefined') {
        return null;
    }

    const session = internals.readSession();

    if (!session) {
        return null;
    }

    if (internals.isExpired(session)) {
        clearActiveSession();
        return null;
    }

    if (session.submittedAt) {
        clearActiveSession();
        return null;
    }

    return session;
}

function hasActiveSession() {

    return !!getActiveSession();
}

function saveSession(session = {}) {

    if (typeof window === 'undefined') {
        return null;
    }

    const nextSession = {
        ...session,
        activatedAt: session.activatedAt || new Date().toISOString()
    };

    Storage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    internals.dispatchUpdate();

    return nextSession;
}

function clearActiveSession() {

    if (typeof window === 'undefined') {
        return;
    }

    Storage.removeItem(STORAGE_KEY);
    internals.dispatchUpdate();
}

function activateFromLocation(location = typeof window === 'undefined' ? null : window.location) {

    if (!location) {
        return null;
    }

    return activateFromUrl(location.href);
}

function activateFromUrl(url) {

    const parsedDebugUrl = internals.parseDebugUrl(url);

    if (!parsedDebugUrl) {
        return null;
    }

    const activeSession = getActiveSession();

    if (activeSession?.code === parsedDebugUrl.code) {
        return activeSession;
    }

    const pendingSession = saveSession({
        id: `pending:${parsedDebugUrl.code}`,
        source: parsedDebugUrl.source,
        audience: 'support',
        status: 'pending-resolve',
        code: parsedDebugUrl.code,
        enabledConsoles: ['support'],
        enabledServices: ['auth', 'communication'],
        expiresAt: internals.getDefaultExpiresAt(),
        submitUrl: '/support/debug-reports',
        originalUrl: parsedDebugUrl.originalUrl
    });

    internals.resolveSession(parsedDebugUrl);

    return pendingSession;
}

async function submitReport({ note, report, metadata } = {}) {

    const session = getActiveSession();
    const api = internals.getSupportDebugApi();

    if (!api?.submitDebugReport) {
        throw new Error('Support debug API is not available');
    }

    const result = await api.submitDebugReport({
        debugSessionId: session?.id?.startsWith('pending:') ? undefined : session?.id,
        code: session?.code,
        audience: session?.audience || 'dev',
        note,
        platform: internals.getPlatform(),
        appVersion: process.env.APP_VERSION || process.env.VERSION || '',
        route: internals.getCurrentRoute(),
        userAgent: typeof window === 'undefined' ? '' : window.navigator?.userAgent,
        report,
        metadata: {
            ...metadata,
            clientDebugSession: session || null
        }
    });

    if (session) {
        saveSession({
            ...session,
            status: 'submitted',
            submittedAt: new Date().toISOString(),
            reportId: result.reportId
        });

        clearActiveSession();
    }

    internals.dispatchThankYou(result);

    return result;
}

function getDebugCodeFromLocation(location = typeof window === 'undefined' ? null : window.location) {

    if (!location) {
        return null;
    }

    return getDebugCodeFromUrl(location.href);
}

function getDebugCodeFromUrl(url) {

    return internals.parseDebugUrl(url)?.code || null;
}

function hasDebugLinkInLocation(location) {

    return !!getDebugCodeFromLocation(location);
}

function isDebugUrl(url) {

    return !!internals.parseDebugUrl(url);
}

function getConsoleActivationSnapshot() {

    const session = getActiveSession();

    return {
        hasActiveSession: !!session,
        session,
        hasDebugLink: hasDebugLinkInLocation()
    };
}

internals.resolveSession = async (parsedDebugUrl) => {

    const api = internals.getSupportDebugApi();

    if (!api?.resolveDebugSession) {
        saveSession({
            ...getActiveSession(),
            status: 'resolve-unavailable',
            resolveError: 'Support debug API is not available'
        });
        return;
    }

    try {
        const resolvedSession = await api.resolveDebugSession({
            code: parsedDebugUrl.code,
            platform: internals.getPlatform(),
            appVersion: process.env.APP_VERSION || process.env.VERSION || '',
            deviceId: internals.getDeviceId(),
            originalUrl: parsedDebugUrl.originalUrl
        });

        saveSession({
            ...resolvedSession,
            code: parsedDebugUrl.code,
            source: parsedDebugUrl.source,
            originalUrl: parsedDebugUrl.originalUrl,
            status: resolvedSession.status || 'activated'
        });
    }
    catch (err) {
        saveSession({
            ...getActiveSession(),
            status: 'resolve-failed',
            resolveError: err?.response?.data?.message || err?.message || String(err)
        });
    }
};

internals.dispatchThankYou = (result = {}) => {

    const reportId = result.reportId;
    const message = reportId
        ? `Thanks — debug info sent to Nearpeer. Report ID: ${reportId}`
        : 'Thanks — debug info sent to Nearpeer.';

    try {
        const { context } = require('app-context');
        const redux = context?.redux?.hooks;
        const pushNotification = context?.actions?.alerts?.notification?.push;

        if (redux?.dispatch && pushNotification) {
            redux.dispatch(pushNotification({ message }));
        }
    }
    catch (err) {
        // Ignore: debug report has already submitted successfully.
    }
};

internals.getSupportDebugApi = () => {

    try {
        return require('app-context').context.api.nearpeer.supportDebug;
    }
    catch (err) {
        return null;
    }
};

internals.parseDebugUrl = (url) => {

    if (typeof url !== 'string' || !url) {
        return null;
    }

    const parsed = internals.safeParseUrl(url);

    if (!parsed) {
        return internals.parseSchemeUrl(url);
    }

    const code = parsed.searchParams.get('debugCode')
        || parsed.searchParams.get('debugSession')
        || parsed.searchParams.get('supportDebug')
        || parsed.searchParams.get('code');

    const isSupportDebugPath = parsed.pathname.includes('/support/debug')
        || parsed.pathname.includes('/debug/support');

    const hasDebugHost = parsed.hostname === 'np-debug.com'
        || parsed.hostname.endsWith('.np-debug.com');

    const isDebugEmailLikePath = parsed.pathname.includes('@np-debug.com');

    if (!code && !hasDebugHost && !isDebugEmailLikePath) {
        return null;
    }

    const pathCode = hasDebugHost
        ? parsed.hostname.split('.')[0]
        : internals.getCodeFromDebugEmailPath(parsed.pathname);

    const finalCode = code || pathCode;

    if (!finalCode || (!isSupportDebugPath && !hasDebugHost && !isDebugEmailLikePath)) {
        return null;
    }

    return {
        code: finalCode,
        source: internals.isCordovaScheme(parsed.protocol) ? 'cordova-deep-link' : 'web-link',
        originalUrl: url
    };
};

internals.parseSchemeUrl = (url) => {

    const schemeMatch = url.match(/^([a-z][a-z0-9+.-]*):\/\/(.*)$/i);

    if (!schemeMatch) {
        return null;
    }

    const [, scheme, rest] = schemeMatch;
    const normalizedUrl = `https://nearpeer.local/${String(rest || '').replace(/^\/+/, '')}`;

    const parsed = internals.safeParseUrl(normalizedUrl);

    if (!parsed) {
        return null;
    }

    const parsedDebugUrl = internals.parseDebugUrl(parsed.toString());

    if (!parsedDebugUrl) {
        return null;
    }

    return {
        ...parsedDebugUrl,
        source: 'cordova-deep-link',
        originalUrl: url,
        scheme
    };
};

internals.safeParseUrl = (url) => {

    try {
        return new URL(url);
    }
    catch (err) {
        return null;
    }
};

internals.getCodeFromDebugEmailPath = (pathname = '') => {

    const match = decodeURIComponent(pathname).match(/([A-Za-z0-9._+-]+)@np-debug\.com/i);

    return match?.[1] || null;
};

internals.isCordovaScheme = (protocol = '') => {

    return ['nearpeer:', 'nearpeerteams:', 'nearpeerstaging:'].includes(protocol);
};

internals.getDefaultExpiresAt = () => {

    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + 30);

    return expiresAt.toISOString();
};

internals.getCurrentRoute = () => {

    if (typeof window === 'undefined') {
        return '';
    }

    return `${window.location?.pathname || ''}${window.location?.search || ''}${window.location?.hash || ''}`;
};

internals.getPlatform = () => {

    if (typeof window === 'undefined') {
        return 'unknown';
    }

    if (window.cordova?.platformId) {
        return window.cordova.platformId;
    }

    return 'web';
};

internals.getDeviceId = () => {

    if (typeof window === 'undefined') {
        return '';
    }

    return window.device?.uuid || '';
};

internals.readSession = () => {

    try {
        const value = Storage.getItem(STORAGE_KEY);

        return value ? JSON.parse(value) : null;
    }
    catch (err) {
        return null;
    }
};

internals.isExpired = (session = {}) => {

    if (!session.expiresAt) {
        return false;
    }

    return Date.now() >= new Date(session.expiresAt).getTime();
};

internals.dispatchUpdate = () => {

    try {
        window.dispatchEvent(new Event('nearpeer:client-debug:update'));
    }
    catch (err) {
        // Ignore unsupported event constructors.
    }
};
